import { themeConfig } from '../../theme';
import { cn } from '../../lib/utils';

export type PublicBrandingNavProps = {
  /** When true, bar stays fixed at the top of the viewport. */
  sticky?: boolean;
  /** Signed view URL for shelter logo; omit for Friends for Life default branding. */
  shelterLogoUrl?: string | null;
  /** Shelter website; when a valid http(s) URL, the shelter logo opens in a new tab. */
  shelterUrl?: string | null;
  /** Used for external logo link accessible name. */
  shelterName?: string;
  shelterLogoLoading?: boolean;
  className?: string;
};

const defaultLogoSrc = themeConfig.logos.brandingHorizontal;
const defaultLogoAlt = `${themeConfig.brand.shortName} logo`;

function normalizeExternalHttpUrl(
  raw: string | null | undefined,
): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const value = raw.trim();
  return /^https?:\/\//i.test(value) ? value : undefined;
}

/**
 * Branding-only public header (Figma TOP-NAV-BAR). Shelter logos may link to the
 * shelter website when `shelterUrl` is configured; default FFL branding is not linked.
 */
export function PublicBrandingNav({
  sticky = false,
  shelterLogoUrl,
  shelterUrl,
  shelterName,
  shelterLogoLoading = false,
  className,
}: PublicBrandingNavProps) {
  const showShelterLogo = Boolean(shelterLogoUrl);
  const showDefaultLogo = !showShelterLogo && !shelterLogoLoading;
  const externalShelterUrl = normalizeExternalHttpUrl(shelterUrl);
  const shelterLogoAriaLabel = shelterName
    ? `${shelterName} website`
    : 'Visit shelter website';

  const shelterLogoImage = showShelterLogo ? (
    // eslint-disable-next-line @next/next/no-img-element -- short-lived signed S3 URL
    <img
      src={shelterLogoUrl!}
      alt='Shelter logo'
      className='max-h-10 max-w-[min(100%,14rem)] object-contain object-center sm:max-h-11 sm:object-left'
    />
  ) : null;

  return (
    <header
      className={cn(
        'w-full border-b border-neutral-200/80 bg-white text-neutral-900',
        sticky && 'sticky top-0 z-40',
        className,
      )}
    >
      <div className='mx-auto flex h-16 w-full max-w-7xl items-center justify-center px-4 sm:h-19 sm:justify-start sm:px-6 lg:px-8'>
        <div className='flex min-h-10 min-w-34 items-center justify-center sm:min-h-11 sm:min-w-42 sm:justify-start'>
          {showShelterLogo && externalShelterUrl ? (
            <a
              href={externalShelterUrl}
              target='_blank'
              rel='noopener noreferrer'
              aria-label={shelterLogoAriaLabel}
              className='rounded-lg outline-none transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
            >
              {shelterLogoImage}
            </a>
          ) : showShelterLogo ? (
            shelterLogoImage
          ) : shelterLogoLoading ? (
            <span
              className='block h-10 w-36 animate-pulse rounded-lg bg-neutral-200 sm:h-11 sm:w-44'
              role='status'
              aria-label='Loading shelter logo'
            />
          ) : showDefaultLogo ? (
            // eslint-disable-next-line @next/next/no-img-element -- static brand SVG
            <img
              src={defaultLogoSrc}
              alt={defaultLogoAlt}
              width={182}
              height={81}
              className='h-9 w-auto max-w-[min(100%,11.5rem)] object-contain object-center sm:h-11 sm:max-w-45.5 sm:object-left'
            />
          ) : null}
        </div>
      </div>
    </header>
  );
}
